import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Stack,
  Typography,
  Chip,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Divider,
} from "@mui/material";
import { GroupAdd, ArrowBack, PersonSearch } from "@mui/icons-material";
import toast from "react-hot-toast";
import { useDuplicateApprovals } from "./useDuplicateApprovals";
import { ORANGE, ORANGE_HOVER, humanizeError } from "../webauthn/components/ui";
import PlayerProfileForm, {
  playerFormFromUserData,
} from "../../components/PlayerProfileForm";
import PlayerMetaLine, { jerseyNumberOf } from "../../components/PlayerMetaLine";
import { positionAlias } from "../../constant/positionList";

/** Human labels for the raise-source values (duplicate-flow spec §12). */
const SOURCE_LABEL = {
  TMS_CLIENT: "TMS Client",
  TMS_ANALYST: "TMS Analyst",
  ANALYST_PORTAL_ANALYST: "Analyst Portal — Analyst",
  ANALYST_PORTAL_ADMIN: "Analyst Portal — Admin",
};

const DUPLICATE_LABEL = {
  SAME_ORGANIZER_FAMILY: "Same organizer family",
  SAME_COUNTRY: "Same country, other organizer",
  DIFFERENT_COUNTRY: "Different country",
  NO_DUPLICATE: "No duplicate",
};

const fmt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch (e) {
    return iso;
  }
};

const fullName = (p) =>
  `${p?.first_name || ""} ${p?.last_name || ""}`.trim() || p?.player_name || "—";

/**
 * Admin / Privileged inbox for duplicate-player approval requests
 * (duplicate-flow spec §11 & §13). Approving creates the player.
 */
export default function DuplicatePlayerApprovalsPage() {
  const history = useHistory();
  const { list, resolve } = useDuplicateApprovals();
  const [busyId, setBusyId] = useState(null);

  const requests = Array.isArray(list.data) ? list.data : [];
  // Only SAME_ORGANIZER_FAMILY is actionable here — SAME_COUNTRY is overridden
  // at source and surfaced in the read-only report instead (spec §3/§4).
  const pending = requests.filter(
    (r) =>
      (r.request_status ?? "PENDING") === "PENDING" &&
      r.duplicate_type !== "SAME_COUNTRY"
  );
  const forbidden = list.error?.response?.status === 403;

  const act = async (request, approve) => {
    setBusyId(request.request_id);
    try {
      const res = await resolve.mutateAsync({ requestId: request.request_id, approve });
      if (res?.request_status === "APPROVED") {
        toast.success(
          res?.user_id ? `Approved — player #${res.user_id} created` : "Request approved"
        );
      } else {
        toast.success("Request rejected");
      }
    } catch (err) {
      toast.error(humanizeError(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Box sx={{ maxWidth: 980, mx: "auto", p: { xs: 2, md: 4 } }}>
      <Stack direction="row" alignItems="center" gap={1.5} sx={{ mb: 0.5 }}>
        <Tooltip title="Back">
          <IconButton
            onClick={() => history.goBack()}
            size="small"
            sx={{ border: "1px solid var(--border-1)", color: "var(--text-1)", flexShrink: 0 }}
          >
            <ArrowBack fontSize="small" />
          </IconButton>
        </Tooltip>
        <GroupAdd sx={{ color: ORANGE }} />
        <Typography sx={{ fontSize: 22, fontWeight: 700, color: "var(--text-1)" }}>
          Duplicate player requests
        </Typography>
      </Stack>
      <Typography sx={{ fontSize: 14, color: "var(--text-2)", mb: 3 }}>
        Compare the requested player against the existing matches. Approving creates the new
        player; rejecting leaves the existing records untouched.
      </Typography>

      {list.isLoading && (
        <Stack alignItems="center" py={4}>
          <CircularProgress sx={{ color: ORANGE }} />
        </Stack>
      )}
      {list.isError && (
        <Alert severity={forbidden ? "info" : "error"} sx={{ borderRadius: "10px" }}>
          {forbidden
            ? "You don't have access to the duplicate player approval queue."
            : humanizeError(list.error)}
        </Alert>
      )}
      {!list.isLoading && !list.isError && pending.length === 0 && (
        <Typography sx={{ color: "var(--text-2)", fontSize: 14 }}>No pending requests.</Typography>
      )}

      <Stack gap={1.5}>
        {pending.map((r) => {
          const requested = r.new_player_created || {};
          const matches = Array.isArray(r.potential_duplicates_details)
            ? r.potential_duplicates_details
            : [];
          const busy = busyId === r.request_id;
          return (
            <Box
              key={r.request_id}
              sx={{
                p: 2,
                borderRadius: "12px",
                backgroundColor: "var(--surface-1)",
                border: "1px solid var(--border-1)",
              }}
            >
              <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                alignItems={{ md: "flex-start" }}
                gap={1.5}
              >
                {/* Requested player */}
                <Stack gap={0.25} sx={{ minWidth: 0 }}>
                  <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                    <Typography sx={{ fontSize: 15, fontWeight: 600, color: "var(--text-1)" }}>
                      {r.player_name || fullName(requested)}
                    </Typography>
                    <Chip
                      size="small"
                      label={DUPLICATE_LABEL[r.duplicate_type] || r.duplicate_type || "—"}
                      sx={{ height: 20, fontSize: 11 }}
                    />
                    {r.creation_blocked && (
                      <Chip
                        size="small"
                        variant="outlined"
                        color="error"
                        label="Blocked"
                        sx={{ height: 20, fontSize: 11 }}
                      />
                    )}
                  </Stack>
                  <PlayerMetaLine
                    size={12}
                    items={[
                      {
                        label: "Organizer Name",
                        value: r.organizer_name || `#${requested.organizer_id ?? "—"}`,
                      },
                      {
                        label: "Team Name",
                        value: r.team_name || `#${requested.team_id ?? "—"}`,
                      },
                    ]}
                  />
                  <PlayerMetaLine
                    size={12}
                    items={[
                      {
                        label: "Raised By",
                        value: `${r.raised_by?.name || (r.raised_by?.id != null ? `#${r.raised_by.id}` : "—")}${
                          r.source ? ` (${SOURCE_LABEL[r.source] || r.source})` : ""
                        }`,
                      },
                      { label: "Raised On", value: fmt(r.raised_on) },
                    ]}
                  />
                </Stack>

                <Stack direction="row" gap={1} sx={{ flexShrink: 0 }}>
                  <Button
                    variant="contained"
                    size="small"
                    disabled={busy}
                    onClick={() => act(r, true)}
                    sx={{
                      textTransform: "none",
                      backgroundColor: ORANGE,
                      "&:hover": { backgroundColor: ORANGE_HOVER },
                    }}
                  >
                    {busy ? "…" : "Approve"}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    disabled={busy}
                    onClick={() => act(r, false)}
                    sx={{ textTransform: "none" }}
                  >
                    Reject
                  </Button>
                </Stack>
              </Stack>

              {/* The Add-New-Player form exactly as the analyst filled it in. */}
              <Divider sx={{ my: 1.5, borderColor: "var(--border-1)" }} />
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: "var(--text-2)", mb: 1 }}>
                Requested player details
              </Typography>
              <PlayerProfileForm
                value={playerFormFromUserData(requested)}
                readOnly
                compact
                showDummy={false}
              />

              {/* Existing matches the analyst wants to bypass */}
              {matches.length > 0 && (
                <>
                  <Divider sx={{ my: 1.5, borderColor: "var(--border-1)" }} />
                  <Stack direction="row" alignItems="center" gap={0.75} sx={{ mb: 0.75 }}>
                    <PersonSearch sx={{ fontSize: 16, color: "var(--text-2)" }} />
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: "var(--text-2)" }}>
                      Existing matches ({matches.length})
                    </Typography>
                  </Stack>
                  <Stack gap={0.5}>
                    {matches.map((m) => (
                      <Stack
                        key={m.id}
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        gap={1}
                        sx={{ px: 1, py: 0.75, borderRadius: "8px", backgroundColor: "var(--surface-2)" }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: 13, color: "var(--text-1)" }}>
                            {m.player_name || fullName(m)}
                          </Typography>
                          <PlayerMetaLine
                            items={[
                              { label: "Player ID", value: m.id },
                              {
                                label: "Organizer Name",
                                value: m.organizer_name || `#${m.organizer_id ?? "—"}`,
                              },
                              {
                                label: "Team Name",
                                value: m.team_name || `#${m.team_id ?? "—"}`,
                              },
                              { label: "Jersey Number", value: jerseyNumberOf(m) },
                              { label: "Position", value: positionAlias(m.preferred_position) },
                            ]}
                          />
                        </Box>
                        {m.fuzz_ratio != null && (
                          <Chip
                            size="small"
                            label={`Name Match ${m.fuzz_ratio}%`}
                            sx={{ height: 20, fontSize: 11, flexShrink: 0 }}
                          />
                        )}
                      </Stack>
                    ))}
                  </Stack>
                </>
              )}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}
